// components/CountryTable.js - Table display for country summary
import React, { useState } from 'react';
import { BarChart3, MapPin, Phone, Copy, CheckCircle } from 'lucide-react';
import { styles } from '../styles';

const CountryTable = ({ countryData, analysisInfo, countryFilter, onCountryClick }) => {
    const [copyAllSuccess, setCopyAllSuccess] = useState(false);

    // Copy all ranges from all countries in the table (RAW FORMAT ONLY)
    const copyAllCountryRanges = async () => {
        if (!countryData || countryData.length === 0) {
            return;
        }

        try {
            // Collect all ranges from all countries (no headers, just raw ranges)
            const allRanges = [];
            let totalRanges = 0;

            countryData.forEach(country => {
                if (country.ranges && country.ranges.length > 0) {
                    // Add only the raw ranges (no country headers)
                    allRanges.push(...country.ranges);
                    totalRanges += country.ranges.length;
                }
            });

            const allRangesText = allRanges.join('\n');

            // Copy to clipboard
            await navigator.clipboard.writeText(allRangesText);

            // Show success feedback
            setCopyAllSuccess(true);
            console.log(`✅ Copied ${totalRanges} raw ranges from ${countryData.length} countries to clipboard`);

            // Reset success status after 3 seconds
            setTimeout(() => {
                setCopyAllSuccess(false);
            }, 3000);

        } catch (error) {
            console.error('❌ Failed to copy all ranges:', error);

            // Fallback for older browsers
            try {
                const allRanges = [];
                countryData.forEach(country => {
                    if (country.ranges && country.ranges.length > 0) {
                        allRanges.push(...country.ranges);
                    }
                });

                const textArea = document.createElement('textarea');
                textArea.value = allRanges.join('\n');
                document.body.appendChild(textArea);
                textArea.select();
                document.execCommand('copy');
                document.body.removeChild(textArea);

                setCopyAllSuccess(true);
                setTimeout(() => setCopyAllSuccess(false), 3000);
                console.log('✅ Copied all raw ranges using fallback method');
            } catch (fallbackError) {
                console.error('❌ Fallback copy also failed:', fallbackError);
                alert('Failed to copy all ranges to clipboard');
            }
        }
    };

    return (
        <div style={styles.tableContainer}>
            <div style={styles.tableHeader}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                    <h2 style={styles.tableTitle}>
                        <BarChart3 size={24} style={{ marginRight: '8px', color: '#a855f7' }} />
                        Country Summary ({countryData.length} unique destinations)
                    </h2>

                    {/* Copy All Ranges Button for main table */}
                    <button
                        onClick={copyAllCountryRanges}
                        disabled={!countryData || countryData.length === 0}
                        style={{
                            backgroundColor: copyAllSuccess ? '#10b981' : '#f59e0b',
                            color: 'white',
                            border: 'none',
                            borderRadius: '8px',
                            padding: '12px 20px',
                            cursor: countryData && countryData.length > 0 ? 'pointer' : 'not-allowed',
                            fontSize: '0.875rem',
                            fontWeight: '600',
                            transition: 'all 0.2s ease',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            opacity: countryData && countryData.length > 0 ? 1 : 0.6,
                            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
                        }}
                        onMouseOver={(e) => {
                            if (countryData && countryData.length > 0 && !copyAllSuccess) {
                                e.target.style.backgroundColor = '#d97706';
                                e.target.style.transform = 'translateY(-1px)';
                                e.target.style.boxShadow = '0 4px 8px rgba(245, 158, 11, 0.3)';
                            }
                        }}
                        onMouseOut={(e) => {
                            if (!copyAllSuccess) {
                                e.target.style.backgroundColor = '#f59e0b';
                                e.target.style.transform = 'translateY(0)';
                                e.target.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.1)';
                            }
                        }}
                    >
                        {copyAllSuccess ? (
                            <>
                                <CheckCircle size={18} />
                                All Ranges Copied!
                            </>
                        ) : (
                            <>
                                <Copy size={18} />
                                Copy All Ranges ({countryData.reduce((total, country) => total + (country.ranges ? country.ranges.length : 0), 0)})
                            </>
                        )}
                    </button>
                </div>

                {/* Enhanced filter display */}
                {analysisInfo && (
                    <div style={{ marginBottom: '15px', color: '#d1d5db', fontSize: '0.9rem' }}>
                        <strong>Analysis Results:</strong> {analysisInfo.total_records.toLocaleString()} total records processed
                        {analysisInfo.search_info.search_text && (
                            <span> | Search: "{analysisInfo.search_info.search_text}"</span>
                        )}
                        {analysisInfo.search_info.origin_filter && (
                            <span> | Origin: "{analysisInfo.search_info.origin_filter}"</span>
                        )}
                        {countryFilter && countryFilter !== 'All Countries' && (
                            <span> | Country: "{countryFilter}"</span>
                        )}
                    </div>
                )}

                <div style={{ overflowX: 'auto' }}>
                    <table style={styles.table}>
                        <thead>
                        <tr>
                            <th style={{ ...styles.tableHeaderCell, fontSize: '1rem' }}>
                                <MapPin size={16} style={{ marginRight: '6px' }} />
                                Country
                            </th>
                            <th style={{ ...styles.tableHeaderCell, fontSize: '1rem' }}>Count</th>
                            <th style={{ ...styles.tableHeaderCell, fontSize: '1rem' }}>
                                <Phone size={16} style={{ marginRight: '6px' }} />
                                Unique Ranges
                            </th>
                            <th style={{ ...styles.tableHeaderCell, fontSize: '1rem' }}>Action</th>
                        </tr>
                        </thead>
                        <tbody>
                        {countryData.map((item, index) => (
                            <tr
                                key={index}
                                style={styles.tableRow}
                                onMouseOver={(e) => {
                                    e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.05)';
                                }}
                                onMouseOut={(e) => {
                                    e.currentTarget.style.backgroundColor = 'transparent';
                                }}
                            >
                                <td style={{ ...styles.tableCell, ...styles.tableCellWhite, fontSize: '1rem' }}>
                                    {item.country}
                                </td>
                                <td style={{ ...styles.tableCell, ...styles.tableCellWhite, fontWeight: 'bold', fontSize: '1rem' }}>
                                    {item.count.toLocaleString()} times
                                </td>
                                <td style={{ ...styles.tableCell, ...styles.tableCellGray, fontSize: '1rem' }}>
                                    {item.unique_ranges} ranges
                                </td>
                                <td style={{ ...styles.tableCell, fontSize: '1rem' }}>
                                    <button
                                        onClick={() => onCountryClick(item)}
                                        style={{
                                            backgroundColor: '#dc2626',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '6px',
                                            padding: '8px 16px',
                                            cursor: 'pointer',
                                            fontSize: '0.875rem',
                                            fontWeight: '500',
                                            transition: 'all 0.2s ease',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '4px'
                                        }}
                                        onMouseOver={(e) => {
                                            e.target.style.backgroundColor = '#b91c1c';
                                            e.target.style.transform = 'translateY(-1px)';
                                        }}
                                        onMouseOut={(e) => {
                                            e.target.style.backgroundColor = '#dc2626';
                                            e.target.style.transform = 'translateY(0)';
                                        }}
                                    >
                                        <Phone size={14} />
                                        View Ranges
                                    </button>
                                </td>
                            </tr>
                        ))}

                        {countryData.length === 0 && (
                            <tr>
                                <td colSpan="4" style={{ ...styles.tableCell, textAlign: 'center', padding: '40px', opacity: 0.7 }}>
                                    No countries found matching the current filters.
                                </td>
                            </tr>
                        )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default CountryTable;